import React from 'react';

import LegalPage from './legalPage';

// Full Terms of Use copy. Rendered inside the shared LegalPage scaffold (top bar +
// header + footer). Last updated date is a plain constant — bump it when the copy changes.
const LAST_UPDATED = 'Oct 2, 2026';

const styles = {
    meta: {
        color: '#828282',
        fontSize: '0.9rem',
        margin: '0 0 28px 0',
    },
    intro: {
        color: '#4F4F4F',
        fontSize: '0.95rem',
        lineHeight: '1.6',
        margin: '0 0 24px 0',
    },
    section: {
        margin: '0 0 24px 0',
    },
    heading: {
        color: '#4F4F4F',
        fontSize: '1rem',
        fontWeight: 'bold',
        margin: '0 0 8px 0',
    },
    paragraph: {
        color: '#4F4F4F',
        fontSize: '0.95rem',
        lineHeight: '1.6',
        margin: '0 0 12px 0',
    },
    list: {
        color: '#4F4F4F',
        fontSize: '0.95rem',
        lineHeight: '1.6',
        margin: '0 0 12px 0',
        paddingLeft: '24px',
    },
    listItem: {
        margin: '0 0 8px 0',
    },
};

const TermsOfUse = (props) => (
    <LegalPage {...props} title="Terms of Use">
        <p style={styles.meta}>Last Updated: {LAST_UPDATED}</p>

        <p style={styles.intro}>
            Welcome to the tool. By registering an account, signing in, accessing (whether logged in or logged out),
            browsing any page, viewing any content, interacting with any element, participating in and/or hosting any
            (&ldquo;jam&rdquo;), creating any (&ldquo;card&rdquo;) and any card (&ldquo;deck&rdquo;), building any
            (&ldquo;template&rdquo;), setting up any (&ldquo;canvas&rdquo;) or engaging in any and all other possible
            activities on this tool&rsquo;s site, you agree to be bound by these Terms of Use.
        </p>

        <div style={styles.section}>
            <p style={styles.heading}>1. Purpose &amp; Evaluation Period</p>
            <p style={styles.paragraph}>
                This tool is provided primarily for evaluation, testing, and study purposes during the active evaluation
                period running from <strong>September 2026 through November 2026</strong>. Participant usage is collected
                and analyzed for the purposes of but not limited to the study of card-based interactive concepts, feature
                scoping, commercialization potential and shaping the future development of the tool.
            </p>
        </div>

        <div style={styles.section}>
            <p style={styles.heading}>2. No Guarantee of Service Continuity or Data Retention</p>
            <p style={styles.paragraph}>
                Following the conclusion of this evaluation period in November 2026, we reserve the absolute right to
                modify, suspend, or permanently terminate the tool at any time without prior notice.
            </p>
            <ul style={styles.list}>
                <li style={styles.listItem}>
                    <strong>Data Deletion:</strong> All user accounts, jams, data, and user-built templates may be
                    permanently deleted at the end of this evaluation period.
                </li>
                <li style={styles.listItem}>
                    <strong>Evolution of the Tool:</strong> We may, at our sole discretion, choose to extend this test
                    environment, transition the tool to a commercial product, migrate to a new domain name, or upgrade to
                    an encrypted connection (HTTPS). We are under no obligation to preserve accounts or data across these
                    transitions.
                </li>
            </ul>
        </div>

        <div style={styles.section}>
            <p style={styles.heading}>3. Data Security &amp; HTTPS Acknowledgment</p>
            <p style={styles.paragraph}>
                <strong>The tool is currently hosted over an unencrypted connection (HTTP).</strong> You acknowledge that
                data transmitted through the tool is not encrypted and may be vulnerable to interception. Do not upload
                sensitive, personal, propietary, or confidential data.
            </p>
        </div>

        <div style={styles.section}>
            <p style={styles.heading}>4. Intellectual Property &amp; Templates</p>
            <p style={styles.paragraph}>
                You retain the rights to the raw content (such as text or uploaded images) you input into the tool.
                However, by generating any data, performing any actions, or building layout configurations within the
                tool, you grant us a worldwide, non-exclusive, royalty-free, perpetual, and irrevocable license to use,
                study, analyze, replicate, modify, and integrate any and all activities, inputs, interactions and design
                ideas into the future product versions, <strong>including but not limited to</strong> tracking cursor
                movements, click data, page views, feature engagement, feature development, software engineering, and
                core template offerings. We retain all rights to the underlying software and automation components.
            </p>
            <p style={styles.paragraph}>
                In the event that a user-built layout, template or interaction configuration is selected for an award,
                prize, or special recognition under this Evaluation Program, the participant agrees that acceptance of
                such award constitutes a full and exclusive assignment of all intellectual property rights of that
                specific template layout to us.
            </p>
        </div>

        <div style={styles.section}>
            <p style={styles.heading}>5. User Testing &amp; Evaluation Program</p>
            <p style={styles.paragraph}>
                By using the tool, you are participating as a user in an official <strong>Evaluation Program and User
                Testing Study.</strong> You acknowledge that the primary purpose of your access is to provide operational
                feedback, system data, and user experience telemetry to help shape the development of the tool. Any
                feedback or feature suggestions you submit will automatically become our property without any obligation
                or compensation to you.
            </p>
        </div>

        <div style={styles.section}>
            <p style={styles.heading}>6. Disclaimer &amp; Limitation of Liability</p>
            <p style={styles.paragraph}>
                The tool is provided strictly &ldquo;AS IS&rdquo; and &ldquo;AS AVAILABLE&rdquo;. We offer no warranties
                of any kind. We are not liable for any data loss, security breaches, system downtime, interrupted jams,
                or lost configurations.
            </p>
        </div>

        <div style={styles.section}>
            <p style={styles.heading}>7. Mandatory Legal Compliance</p>
            <p style={styles.paragraph}>
                By accessing the tool, you represent and warrant that you will strictly comply with all applicable local,
                state, national, and international laws, regulations, and statutory directives of Malaysia. You agree not
                to use the tool for any unlawful activities or in any manner that violates Malaysian cybersecurity, data
                transmission, or computer misuse regulations. You are responsible for all your activity on the tool and
                your usage may be logged with data potentially identifying you through use of cookies, browser, operating
                system, IP address, data matching and other means.
            </p>
        </div>
    </LegalPage>
);

export default TermsOfUse;
